import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import type { ErrorLike } from '@apollo/client'

import { IconButton, SendHorizontalIcon } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'

import { channelIconElement } from '../../components/ChannelLegendItem'
import type { FollowersBreakdownSnapshot } from '../../components/ChartSnapshot'
import { SummaryMode } from '../../components/MetricChart'
import type { MetricSeries } from '../../components/MetricChart'
import { NoDataEmptyState } from '../../components/NoDataEmptyState'
import { useExportChartAsPost } from '../../hooks/useExportChartAsPost'
import { captureChartAsBlob } from '../../chartToImage'
import { useRegisterSnapshots } from '../../context'
import { formatDateRange, getNoDataDescription } from '../../utils'
import type { ChannelTimeSeries } from '../hooks/useAllChannelsTimeSeries'

import { FollowersChartSectionView } from './ChartSectionViews'
import { FollowersView } from './ChartViewToggle'
import { toFollowersSeries } from './toFollowersSeries'
import { useMetricChannels } from './useMetricChannels'
import sectionButtonStyles from '../../components/sectionActionButton.module.css'

interface AllChannelsFollowersChartProps {
  channelTimeSeries: ChannelTimeSeries[]
  totalChannelCount: number
  loading: boolean
  refreshing?: boolean
  error?: ErrorLike
  startDate: Date
  endDate: Date
  selectedDateRange?: string
  hasChannelFilter?: boolean
  onClearChannelFilter?: () => void
  /** When false, hides the "Share as Post" button (sharing opens the composer). */
  canCreatePosts?: boolean
}

export function AllChannelsFollowersChart({
  channelTimeSeries,
  totalChannelCount,
  loading,
  refreshing = false,
  error,
  startDate,
  endDate,
  selectedDateRange,
  hasChannelFilter,
  onClearChannelFilter,
  canCreatePosts = true,
}: AllChannelsFollowersChartProps): JSX.Element {
  const [followersView, setFollowersView] = useState<FollowersView>(
    FollowersView.BY_CHANNEL,
  )

  const metricChannels = useMetricChannels({
    channelTimeSeries,
    metric: 'followers',
    totalChannelCount,
    hasChannelFilter,
  })
  const { displayedChannels, channels, paletteIndexById } = metricChannels

  // One over-time series per channel. All-Channels never fetches comparison
  // data, so there's no dashed overlay to build here.
  const chartMetrics: MetricSeries[] = useMemo(
    () =>
      displayedChannels.map((ch, index) => ({
        key: `followers-${ch.channel.id}`,
        title: ch.channel.name,
        icon: channelIconElement(ch.channel.service),
        data: ch.primary.followers.map((p) => ({
          date: p.date,
          value: p.value,
          source: p.source,
        })),
        color: channels[index].color,
        summaryMode: SummaryMode.Latest,
      })),
    [displayedChannels, channels],
  )

  const followersSeries = useMemo(
    () => toFollowersSeries(displayedChannels, paletteIndexById),
    [displayedChannels, paletteIndexById],
  )

  const exportText = useMemo(
    () =>
      channels.length === 1
        ? `Check out how my followers have grown on ${channels[0].name}! 📊`
        : `Check out how my followers have grown across my channels! 📊`,
    [channels],
  )

  const { account } = useAccount()

  const visibleChannelCount = channels.length
  const channelCountLabel = `${visibleChannelCount} ${
    visibleChannelCount === 1 ? 'channel' : 'channels'
  }`

  const showFollowersBreakdown = followersView !== FollowersView.OVER_TIME

  const snapshotBase = {
    channelIds: displayedChannels.map((ch) => ch.channel.id),
    text: exportText,
    heading: 'Followers',
    subheading: `${channelCountLabel} · ${formatDateRange(startDate, endDate)}`,
    channelAvatarUrl: account?.avatar ?? undefined,
    altText: `Buffer Insights chart of Followers across ${channelCountLabel} over ${formatDateRange(startDate, endDate)}.`,
    chartMetrics,
    chartStartDate: startDate,
    chartEndDate: endDate,
    chartSingleAxis: true,
    chartYAxisLabel: 'Followers',
  }

  const byChannelBreakdown: FollowersBreakdownSnapshot = {
    variant: 'byChannel',
    series: followersSeries,
  }
  const growthBreakdown: FollowersBreakdownSnapshot = {
    variant: 'growth',
    series: followersSeries,
  }

  // "Share as Post" keeps the currently-selected view.
  const chartCaptureOptions = {
    ...snapshotBase,
    followersBreakdown: showFollowersBreakdown
      ? followersView === FollowersView.GROWTH
        ? growthBreakdown
        : byChannelBreakdown
      : undefined,
  }

  const { onExport, isExporting } = useExportChartAsPost(chartCaptureOptions)

  const hasAnyData = chartMetrics.length > 0

  const snapshotsAvailable = hasAnyData && !loading && !error && !refreshing
  useRegisterSnapshots([
    {
      id: 'followers-bar',
      filename: 'followers-bar.png',
      order: 10,
      available: snapshotsAvailable,
      getBlob: async (): Promise<Blob> =>
        (
          await captureChartAsBlob({
            ...snapshotBase,
            followersBreakdown: byChannelBreakdown,
          })
        ).blob,
    },
    {
      id: 'followers-line',
      filename: 'followers-line.png',
      order: 11,
      available: snapshotsAvailable,
      getBlob: async (): Promise<Blob> =>
        (
          await captureChartAsBlob({
            ...snapshotBase,
            followersBreakdown: undefined,
          })
        ).blob,
    },
    {
      id: 'followers-growth',
      filename: 'followers-growth.png',
      order: 12,
      available: snapshotsAvailable,
      getBlob: async (): Promise<Blob> =>
        (
          await captureChartAsBlob({
            ...snapshotBase,
            followersBreakdown: growthBreakdown,
          })
        ).blob,
    },
  ])

  return (
    <FollowersChartSectionView
      metricChannels={metricChannels}
      totalChannelCount={totalChannelCount}
      series={followersSeries}
      view={followersView}
      onViewChange={setFollowersView}
      startDate={startDate}
      endDate={endDate}
      dateLabel={formatDateRange(startDate, endDate)}
      loading={loading}
      error={error}
      refreshing={refreshing}
      emptyState={
        <NoDataEmptyState
          description={getNoDataDescription(
            selectedDateRange,
            hasChannelFilter,
          )}
          onClearChannelFilter={onClearChannelFilter}
          size="small"
        />
      }
      headerExtraActions={
        canCreatePosts && (
          <IconButton
            className={sectionButtonStyles.neutralBackground}
            size="medium"
            onClick={onExport}
            loading={isExporting}
            label={
              isExporting ? 'Sharing Followers…' : 'Share as Post for Followers'
            }
            tooltip={isExporting ? 'Sharing…' : 'Share as Post'}
          >
            <SendHorizontalIcon />
          </IconButton>
        )
      }
    />
  )
}
